import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { getApiBaseUrl, getAuthHeaders } from "../utils/apiHelpers";
import WalkInPaymentModal from "./WalkInPaymentModal";

/**
 * WalkInButton — a self-contained "New walk-in" affordance for ANY role page.
 *
 * Why a wrapper rather than wiring the modal into each page: there are seven
 * surfaces (four dashboards + three bookings pages) and they fetch their data in
 * different ways. This component owns the open/close state and fetches the
 * services (and, for R1/R2, the professionals) itself, so each page needs one
 * line and cannot drift from the others.
 *
 * Props:
 *   requireProvider  R1/R2 must name the delivering professional. R3/R4/R7 leave
 *                    this false and the modal defaults to themselves.
 *   shopId           The location this button should default to (R1/R2).
 *   locations        Every location the caller may use. An R1 with more than one gets
 *                    a location picker inside the modal; R2 is pinned to a single
 *                    location by design and gets no picker.
 *   role             Caller's role code, used to decide who sees the picker.
 *   label            Button text. Defaults to "New walk-in".
 *   className        Optional class so each page can match its own button style.
 *   onCompleted      Optional callback after a successful walk-in.
 *   reopenBookingId  A walk-in awaiting payment to REOPEN instead of starting a new
 *                    one. The page sets this when the desk asks to finish an unpaid
 *                    walk-in from the list; `onClose` fires when the modal closes so
 *                    the page can clear it again.
 *   onClose          Called when the modal closes.
 */
function WalkInButton({
  requireProvider = false,
  shopId = null,
  locations = [],
  role = "",
  label = "New walk-in",
  className = "",
  onCompleted,
  reopenBookingId = null,
  onClose,
}) {
  const [isOpen, setIsOpen] = useState(false);
  // May this caller take a walk-in AT ALL? `null` = still asking. The server answers
  // with the same module POST /walk-in refuses with (utils/walkInAccess), so the
  // button and the API cannot disagree — a suspended business, a lapsed trial, an R7
  // in rent arrears or a deactivated profile simply never sees the menu.
  const [access, setAccess] = useState(null);
  const [services, setServices] = useState([]);
  // Front desk: the PROFESSIONAL list is fetched by the modal, not here. The location
  // can be changed inside the modal and the list has to follow it, so exactly one
  // component must own that fetch or two lists would compete for the same dropdown.
  const [staffId, setStaffId] = useState(null);
  // The caller's own role, for the roles that deliver the walk-in themselves. The
  // modal needs it to decide whether to offer group members (R3 only).
  const [selfRole, setSelfRole] = useState("");
  // The professional's timezone. The modal needs it to work out what "today" is —
  // the UTC date rolls over to tomorrow in the evening for US timezones.
  const [timezone, setTimezone] = useState(null);
  const [loadError, setLoadError] = useState("");

  /** Load everything the modal needs BEFORE opening, so it opens populated. */
  const openModal = useCallback(async () => {
    setLoadError("");
    try {
      const base = getApiBaseUrl();
      const headers = getAuthHeaders();

      const servicesRes = await axios.get(`${base}/api/staff/services`, {
        headers,
      });
      const active = (servicesRes.data?.services || []).filter(
        (s) => s.isActive !== false,
      );
      setServices(active);

      if (requireProvider) {
        // Only refuse when there is genuinely nowhere to put the walk-in. The modal
        // resolves a location from `locations` even when this page could not, so a
        // missing `shopId` alone must not block an owner.
        if (!shopId && locations.length === 0) {
          setLoadError(
            "No location is available for this walk-in. Ask your business owner to assign you to one.",
          );
          return;
        }
        setTimezone(null); // each provider carries its own timezone
      } else {
        // R3/R4/R7 default to themselves, but the SLOT PICKER still needs their
        // staff id to ask for that professional's availability.
        try {
          const profileRes = await axios.get(`${base}/api/staff/profile`, {
            headers,
          });
          const profile = profileRes.data || {};
          setStaffId(
            profile?.staff?._id || profile?.staffId || profile?._id || null,
          );
          // The profile is authoritative; localStorage is the fallback for an older
          // profile response that predates the `role` field.
          setSelfRole(
            profile?.staff?.role ||
              localStorage.getItem("ogestyle_role") ||
              "",
          );
          setTimezone(profile?.staff?.timezone || null);
        } catch (profileErr) {
          setStaffId(null);
          setTimezone(null);
        }
      }

      setIsOpen(true);
    } catch (err) {
      setLoadError(
        err?.response?.data?.message ||
          "Unable to load the details needed for a walk-in.",
      );
    }
  }, [requireProvider, shopId, locations.length]);

  /**
   * Ask ONCE, before offering anything. A refused caller gets no button — which is
   * the point: the old flow made a suspended professional fill in the whole form
   * before the server said no.
   *
   * A failed check is treated as allowed: the POST re-runs the same rule and is the
   * authority, so a network blip must not hide a legitimate desk's walk-in button.
   */
  useEffect(() => {
    let cancelled = false;
    const checkAccess = async () => {
      try {
        const res = await axios.get(
          `${getApiBaseUrl()}/api/bookings/walk-in/access`,
          {
            headers: getAuthHeaders(),
            params: shopId ? { shopId } : undefined,
          },
        );
        if (!cancelled) setAccess(res.data || { allowed: true });
      } catch (err) {
        if (!cancelled) setAccess({ allowed: true });
      }
    };
    checkAccess();
    return () => {
      cancelled = true;
    };
  }, [shopId]);

  /**
   * A walk-in the desk asked to reopen, from the bookings list.
   *
   * Opened directly rather than through `openModal`, because that path loads the
   * service catalogue the CREATE form needs - none of which a reopen uses. The modal
   * reads the walk-in itself.
   *
   * Keyed on `isOpen` as well as the id, so closing and reopening the SAME walk-in
   * re-reads it rather than showing a stale copy.
   */
  useEffect(() => {
    if (!reopenBookingId) return;
    // Finishing an unpaid walk-in is the same door — a refused caller gets nothing.
    if (access && access.allowed === false) return;
    setLoadError("");
    setIsOpen(true);
  }, [reopenBookingId, access]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    if (typeof onClose === "function") onClose();
  }, [onClose]);

  // Not allowed (or still asking): render nothing at all. No button, no menu entry.
  if (!access || access.allowed === false) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className={className || "walkin-launch-btn"}
        onClick={openModal}
      >
        + {label}
      </button>

      {loadError ? <div className="walkin-launch-error">{loadError}</div> : null}

      <WalkInPaymentModal
        isOpen={isOpen}
        onClose={handleClose}
        onCompleted={(booking) => {
          if (typeof onCompleted === "function") onCompleted(booking);
        }}
        services={services}
        requireProvider={requireProvider}
        presetStaffId={staffId}
        defaultTimezone={timezone}
        reopenBookingId={reopenBookingId}
        // The front-desk modal fetches the professionals and the chosen
        // professional's services itself, because both depend on the location - and
        // an R1 can change that location from inside the modal. /api/staff/services
        // returns "MY services" and is empty for an owner or admin, which is why the
        // service dropdown used to come up blank.
        shopId={shopId}
        locations={locations}
        canChooseLocation={role === "R1"}
        // R3/R4/R7 deliver the walk-in themselves, so for them the "delivering
        // professional's role" is simply the caller's role.
        providerSelfRole={requireProvider ? "" : selfRole}
      />
    </>
  );
}

export default WalkInButton;
